import React from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { usePlatform } from '../../contexts/PlatformContext';
import { STAGE_META, nextStage } from '../../utils/stages';
import type { ApplicationRecord } from '../../types/domain';
interface AdvanceStageDialogProps {
  record: ApplicationRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gateCleared: boolean;
  outstandingCount: number;
}
export function AdvanceStageDialog({
  record,
  open,
  onOpenChange,
  gateCleared,
  outstandingCount
}: AdvanceStageDialogProps) {
  const {
    advanceStage
  } = usePlatform();
  const [rationale, setRationale] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const target = nextStage(record.track, record.stage);
  React.useEffect(() => {
    if (open) {
      setRationale('');
      setError(null);
    }
  }, [open]);
  function submit() {
    if (!gateCleared && rationale.trim().length < 15) {
      setError('An override requires a recorded rationale of at least 15 characters.');
      return;
    }
    advanceStage(record.id, {
      override: !gateCleared,
      rationale: rationale.trim()
    });
    onOpenChange(false);
    toast.success(target ? `${record.ref} advanced to ${STAGE_META[target].label}` : `${record.ref} updated`, {
      description: gateCleared ? 'Gate cleared on all artefacts and sign-offs.' : 'Gate override recorded on the audit timeline.'
    });
  }
  return <Dialog open={open} onOpenChange={onOpenChange} data-id="element-485" data-mp="2h9h5oi3inzp">
      <DialogContent data-id="element-486" data-mp="3tp435ojpinz5">
        <DialogHeader data-id="element-487" data-mp="3tpnbcdjk1xzz">
          <DialogTitle data-id="element-488" data-mp="3to2nt7vnrfbm">
            {gateCleared ? 'Advance to next stage' : 'Override gate and advance'}
          </DialogTitle>
          <DialogDescription data-id="element-489" data-mp="q8wzhi1akqqd">
            {target ? `${record.ref} will move from ${STAGE_META[record.stage].label} to ${STAGE_META[target].label}.` : 'This record is at the final stage of its lifecycle.'}
          </DialogDescription>
        </DialogHeader>

        {!gateCleared && <div className="rounded-sm border border-uae-red/30 bg-uae-red/5 px-3 py-2.5 text-[12px] leading-relaxed text-uae-red" data-id="element-490" data-mp="10c2dih4vza5p">
            {outstandingCount} gate item{outstandingCount === 1 ? '' : 's'} not yet met. Advancing
            now is an override and will be attributed to you on the audit timeline.
          </div>}

        <div className="space-y-1.5" data-id="element-491" data-mp="vbxsqewy3t9o">
          <Label htmlFor="advance-rationale" data-id="element-492" data-mp="heraclg4vwkz">
            {gateCleared ? 'Advancement note (optional)' : 'Override rationale (required)'}
          </Label>
          <Textarea id="advance-rationale" value={rationale} onChange={event => setRationale(event.target.value)} placeholder={gateCleared ? 'Add any note for the audit record' : 'Explain why the gate is being overridden and how the outstanding items will be closed'} rows={4} aria-invalid={Boolean(error)} data-id="element-493" data-mp="lhxgzu75rprz" />
          {error && <p className="text-[12px] text-uae-red" data-id="element-494" data-mp="dxdbmnbg7nmd">{error}</p>}
        </div>

        <DialogFooter data-id="element-495" data-mp="953fgpjqprbn">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-id="element-496" data-mp="94tp30us9i5f">
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!target} data-id="element-497" data-mp="nsojclofvukm">
            {gateCleared ? 'Advance stage' : 'Record override and advance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}